import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import { useInsta360Session } from "providers/Insta360SessionProvider";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import { useToast } from "providers/ToastProvider";
import { useGetActionsQuery } from "app/services/missionStorageApiEnhanced";
import { resolveActiveMissionId } from "lib/mission/activeMission";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";

import { ReactComponent as Insta360Icon } from "assets/svgs/navbar/insta360.svg";

const ControlInsta360Button = () => {
    const { t } = useTranslation("control");
    const { addToast } = useToast();
    const { fsm, busy, baseUrl, startRecord, stopRecord } = useInsta360Session();
    const { executorState, isExecuting, currentMissionId, stepStatuses } = useMissionExecution();

    const connected = fsm === "idle" || fsm === "recording" || fsm === "downloading";
    const recording = fsm === "recording" || fsm === "downloading";

    const activeMissionId = resolveActiveMissionId({
        executorState,
        isExecuting,
        currentMissionId,
    });
    const { data: actionsData } = useGetActionsQuery(
        { missionId: activeMissionId || "" },
        { skip: !activeMissionId }
    );

    const missionLocks360 = useMemo(() => {
        if (!activeMissionId) return false;
        if ((actionsData?.actions || []).some((action) => action.actionType === "Record360")) {
            return true;
        }
        if (
            (executorState?.step_statuses || []).some((step) => step.action_type === "Record360")
        ) {
            return true;
        }
        return Object.values(stepStatuses).some((step) => step.action_type === "Record360");
    }, [activeMissionId, actionsData?.actions, executorState?.step_statuses, stepStatuses]);

    const canStart = Boolean(baseUrl) && fsm === "idle" && !busy && !missionLocks360;
    const canStop = Boolean(baseUrl) && fsm === "recording" && !busy && !missionLocks360;
    const enabled = canStart || canStop;
    const hoverText = missionLocks360
        ? t("buttons.record360MissionLock")
        : recording
          ? t("buttons.record360Stop")
          : t("buttons.record360");

    const onClick = async () => {
        if (!enabled) return;
        try {
            if (canStop) {
                await stopRecord();
            } else {
                await startRecord();
            }
        } catch (err) {
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("buttons.record360Failed")),
            });
        }
    };

    if (!connected) {
        return null;
    }

    return (
        <FunctionalButton
            className={`control-button-type3 ${recording && !missionLocks360 ? "control-button-active" : ""} ${
                !enabled ? "control-button-disable" : ""
            }`}
            icon={
                <Insta360Icon
                    className={`h-6 w-6 shrink-0 ${!enabled ? "text-black70" : ""}`}
                    style={{ width: "1.5rem", height: "1.5rem" }}
                />
            }
            hoverText={hoverText}
            disabled={!enabled}
            aria-pressed={recording}
            onClick={() => void onClick()}
        />
    );
};

export default ControlInsta360Button;
